/**
 * Prepares the form payload by including only fields modified in the form (use
 * react-hook-form's dirtyFields).
 *
 * @remarks The function converts empty strings to null as the server expects.
 * @remarks Handles both simple fields and nested array/object dirty states.
 */
const hasDirtyValue = (value: unknown): boolean => {
  if (value === true) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(hasDirtyValue);
  }

  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(hasDirtyValue);
  }

  return false;
};

export const prepareFormPayload = <FormValues>(
  values: FormValues,
  dirtyFields: Partial<Readonly<Record<keyof FormValues, unknown>>>,
): Partial<FormValues> =>
  Object.keys(values).reduce((acc, key) => {
    const typedKey = key as keyof FormValues;
    const dirtyField = dirtyFields[typedKey];

    if (hasDirtyValue(dirtyField)) {
      if (typeof values[typedKey] === 'string') {
        acc[typedKey] =
          values[typedKey] === ''
            ? null
            : ((values[typedKey] as string).trim() as any);
      } else if (Array.isArray(dirtyField) && Array.isArray(values[typedKey])) {
        acc[typedKey] = (values[typedKey] as unknown[]).filter((_, index) =>
          hasDirtyValue(dirtyField[index]),
        ) as FormValues[keyof FormValues];
      } else {
        acc[typedKey] = values[typedKey];
      }
    }

    return acc;
  }, {} as Partial<FormValues>);
